import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  SkeletonRows,
  THead,
  TableShell,
  Td,
  Th,
  Tr,
  useToast,
} from '@ambernord/ui';
import { Archive as ArchiveIcon, ShieldCheck } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLayout, DataState } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { formatDate } from '../lib/format.js';

interface ArchiveItem {
  id: string;
  type: string;
  category: string | null;
  tags: string[] | null;
  searchText: string | null;
  createdAt: string;
}

interface ExpiringCertificate {
  id: string;
  type: string;
  identifier: string | null;
  reminderAt: string | null;
}

interface PostError {
  error?: string;
}

function buildSearchUrl(text: string, tag: string, category: string): string {
  const params = new URLSearchParams();
  params.set('text', text);
  params.set('tag', tag);
  params.set('category', category);
  return `/api/archive/items?${params.toString()}`;
}

function Items() {
  const { t, i18n } = useTranslation('archive');
  const { toast } = useToast();
  const [items, setItems] = useState<ArchiveItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Search filters
  const [text, setText] = useState('');
  const [tag, setTag] = useState('');
  const [category, setCategory] = useState('');

  // Create form
  const [cType, setCType] = useState('');
  const [cCategory, setCCategory] = useState('');
  const [cTags, setCTags] = useState('');
  const [cSearchText, setCSearchText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(
    async (txt: string, tg: string, cat: string) => {
      setItems(null);
      setError(null);
      const res = await api.get<{ items: ArchiveItem[] }>(buildSearchUrl(txt, tg, cat));
      if (!res.ok) {
        setError(t('items.loadError', { status: res.status }));
        setItems([]);
        return;
      }
      setItems(res.data.items ?? []);
    },
    [t],
  );

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: ArchiveItem[] }>(buildSearchUrl('', '', ''));
      if (!active) return;
      if (!res.ok) {
        setError(t('items.loadError', { status: res.status }));
        setItems([]);
        return;
      }
      setItems(res.data.items ?? []);
    })();
    return () => {
      active = false;
    };
  }, [t]);

  function onSearch(e: FormEvent) {
    e.preventDefault();
    void load(text, tag, category);
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await api.post<PostError>('/api/archive/items', {
        type: cType,
        ...(cCategory ? { category: cCategory } : {}),
        ...(cTags
          ? {
              tags: cTags
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            }
          : {}),
        ...(cSearchText ? { searchText: cSearchText } : {}),
      });
      if (!res.ok) {
        const msg = res.data.error ?? t('errors.requestFailed', { status: res.status });
        setFormError(msg);
        toast(msg, 'error');
        return;
      }
      setCType('');
      setCCategory('');
      setCTags('');
      setCSearchText('');
      toast(t('toast.created'));
      await load(text, tag, category);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader title={t('items.heading')} />
      <CardBody>
        <form
          className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
          onSubmit={onSearch}
          noValidate
        >
          <Field label={t('items.search.text')} htmlFor="arc-text" className="flex-1">
            <Input id="arc-text" value={text} onChange={(e) => setText(e.target.value)} />
          </Field>
          <Field label={t('items.search.tag')} htmlFor="arc-tag">
            <Input id="arc-tag" value={tag} onChange={(e) => setTag(e.target.value)} />
          </Field>
          <Field label={t('items.search.category')} htmlFor="arc-category">
            <Input
              id="arc-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
          </Field>
          <Button type="submit" variant="secondary">
            {t('items.search.submit')}
          </Button>
        </form>

        <div className="mb-6">
          {error ? (
            <DataState loading={false} error={error} empty={false}>
              {null}
            </DataState>
          ) : items === null ? (
            <SkeletonRows rows={4} />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<ArchiveIcon className="h-6 w-6" />}
              title={t('items.empty.title')}
              hint={t('items.empty.hint')}
            />
          ) : (
            <TableShell>
              <THead>
                <tr>
                  <Th>{t('items.columns.type')}</Th>
                  <Th>{t('items.columns.category')}</Th>
                  <Th>{t('items.columns.tags')}</Th>
                  <Th>{t('items.columns.text')}</Th>
                  <Th>{t('items.columns.created')}</Th>
                </tr>
              </THead>
              <tbody>
                {items.map((it) => (
                  <Tr key={it.id}>
                    <Td className="font-medium text-slate-900 dark:text-white">{it.type}</Td>
                    <Td>{it.category ?? '—'}</Td>
                    <Td>
                      {it.tags && it.tags.length > 0 ? (
                        <span className="flex flex-wrap gap-1">
                          {it.tags.map((tg) => (
                            <Badge key={tg} tone="slate">
                              {tg}
                            </Badge>
                          ))}
                        </span>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td>{it.searchText ?? '—'}</Td>
                    <Td>{formatDate(it.createdAt, i18n.language)}</Td>
                  </Tr>
                ))}
              </tbody>
            </TableShell>
          )}
        </div>

        <h3 className="mb-4 text-sm font-medium">{t('items.addHeading')}</h3>
        <form
          className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
          onSubmit={(e) => void onCreate(e)}
          noValidate
        >
          <Field label={t('items.fields.type')} htmlFor="arc-ctype">
            <Input
              id="arc-ctype"
              required
              value={cType}
              onChange={(e) => setCType(e.target.value)}
            />
          </Field>
          <Field label={t('items.fields.category')} htmlFor="arc-ccategory">
            <Input
              id="arc-ccategory"
              value={cCategory}
              onChange={(e) => setCCategory(e.target.value)}
            />
          </Field>
          <Field label={t('items.fields.tags')} htmlFor="arc-ctags">
            <Input id="arc-ctags" value={cTags} onChange={(e) => setCTags(e.target.value)} />
          </Field>
          <Field label={t('items.fields.searchText')} htmlFor="arc-csearchtext" className="flex-1">
            <Input
              id="arc-csearchtext"
              value={cSearchText}
              onChange={(e) => setCSearchText(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('saving') : t('items.submit')}
          </Button>
        </form>
        {formError && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
            {formError}
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function ExpiringCertificates() {
  const { t, i18n } = useTranslation('archive');
  const [items, setItems] = useState<ExpiringCertificate[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: ExpiringCertificate[] }>(
        '/api/archive/certificates/expiring?withinDays=30',
      );
      if (!active) return;
      if (!res.ok) {
        setError(t('expiring.loadError', { status: res.status }));
        setItems([]);
        return;
      }
      setItems(res.data.items ?? []);
    })();
    return () => {
      active = false;
    };
  }, [t]);

  return (
    <Card>
      <CardHeader title={t('expiring.heading')} subtitle={t('expiring.description')} />
      <CardBody>
        {error ? (
          <DataState loading={false} error={error} empty={false}>
            {null}
          </DataState>
        ) : items === null ? (
          <SkeletonRows rows={3} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck className="h-6 w-6" />}
            title={t('expiring.empty.title')}
            hint={t('expiring.empty.hint')}
          />
        ) : (
          <TableShell>
            <THead>
              <tr>
                <Th>{t('expiring.columns.type')}</Th>
                <Th>{t('expiring.columns.identifier')}</Th>
                <Th>{t('expiring.columns.reminder')}</Th>
              </tr>
            </THead>
            <tbody>
              {items.map((c) => (
                <Tr key={c.id}>
                  <Td className="font-medium text-slate-900 dark:text-white">{c.type}</Td>
                  <Td>{c.identifier ?? '—'}</Td>
                  <Td>{formatDate(c.reminderAt, i18n.language)}</Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </CardBody>
    </Card>
  );
}

export default function Archive() {
  const { t } = useTranslation('archive');
  return (
    <AppLayout title={t('title')}>
      <div className="flex flex-col gap-6">
        <Items />
        <ExpiringCertificates />
      </div>
    </AppLayout>
  );
}
